/**
 * Authentication Types
 *
 * Type definitions for authentication store and related functionality
 */

import type { FetchOptions } from 'ofetch'

/**
 * JWT payload structure for decoding the access token
 */
export type JWTPayload = {
  exp: number // Expiration timestamp in seconds since epoch
  iat: number // Issued at timestamp in seconds since epoch
  [key: string]: unknown // Allow other claims
}

/**
 * Auth store state type
 */
export type AuthState = {
  user: import('@openbox/shared-types/lib/auth/Auth').LoggedUserInfo | null
  isAuthenticated: boolean
  tokenExpiresAt: number | null
  isRefreshing: boolean
  /** Flag indicating if session restoration has been attempted on page load */
  sessionRestored: boolean
  /** Flag indicating if a system navigation is in progress (logout, workspace change) */
  isSystemNavigation: boolean
}

/**
 * Extended fetch options with auth retry tracking
 * Used by the auth interceptor to track retry attempts after 401 errors
 */
export interface AuthFetchOptions extends FetchOptions {
  _authRetry?: boolean
  /** When true, the auth interceptor will not handle 401 errors for this request.
   *  The caller is responsible for its own error handling (e.g., during session restoration). */
  _skipAuthRetry?: boolean
}

/**
 * Custom error class used to propagate successful retry data through ofetch's error handling.
 *
 * When a request fails with 401 and is successfully retried after token refresh,
 * we need to return the successful response data to the original caller.
 * Since ofetch's onResponseError hook doesn't support returning a success response directly,
 * we throw this special error type that useSecuredApi catches and unwraps as a success.
 *
 * This is an implementation detail of the 401 retry mechanism and should not be used elsewhere.
 */
export class AuthRetrySuccessError extends Error {
  public readonly data: unknown

  constructor(data: unknown) {
    super('__AUTH_RETRY_SUCCESS__')
    this.name = 'AuthRetrySuccessError'
    this.data = data
  }
}

/**
 * Branch information for workspace switching
 */
export type WorkspaceBranch = {
  id: string
  name: string
}

/**
 * Company information for workspace switching with branches
 */
export type WorkspaceCompany = {
  companyId: string
  companyName: string
  branches: WorkspaceBranch[]
  active: boolean
}
